/**
 * <bundle-builder> — interactive shell for the "Choose The Perfect Bundle"
 * block (snippets/upsell-bundle-builder.liquid).
 *
 * Responsibilities:
 *   - Pick a bundle base from the always-visible radio list. On the
 *     standalone-table template, switching into Bundle mode starts with
 *     nothing preselected; the customer explicitly picks a base, which
 *     reveals the "Personalize your Bundle" block.
 *   - Switch the active bundle (updates mode-card meta, tab visibility,
 *     sold-out / add-to-cart state, and broadcasts a
 *     `bundle-builder:bundle-change` event).
 *   - Switch personalize tabs (N tabs driven by the shop's personalize
 *     catalog, e.g. Chairs / Benches / Panel Storage). The set of tabs
 *     is fully data-driven by `data-bb-config.bundles[i].categories`.
 *   - Pick a radio card inside a tab.
 *   - Open / close the "Chose colors" popover per card and apply the
 *     picked variant (image swap + variant id stamping on the radio).
 *   - Remember the picked product + color per category per bundle
 *     so switching bundles and coming back restores the selection.
 *
 * Cart submission carries `_bb_<key>_variant_id` / `_bb_<key>_qty` line
 * properties per active category. For backward compatibility with the
 * existing Shopify Cart Transform Function the catalog handles `chairs`
 * and `benches` map to the legacy property keys `chair` and `bench`.
 * Other categories (e.g. `panel-storage`) map kebab→snake (`panel_storage`).
 * The Function will need to be generalized before any non-chair/bench
 * category actually ships in a bundle — see categoryToPropertyKey().
 */
// Remembers the customer's base choice (which bundle, or "just the table")
// per section, keyed by section id. A variant-change re-render recreates the
// <bundle-builder> element from fresh server markup (always table-mode on a
// table PDP), so without this the selection would reset on every color swap.
// The module stays loaded across those re-renders, so this survives them.
const BB_LAST_SELECTION = {};

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.sectionId = '';
    this.defaultBundleHandle = '';
    this.currentBundleHandle = '';
    // Active tab handle (catalog category handle, e.g. 'chairs').
    // Set to first present category of the default bundle in connectedCallback().
    this.activeTab = '';
    this.bundlesConfig = [];
    // Per-bundle memory: { bundleHandle: { categoryHandle: { handle: itemHandle, variantId: id } } }
    this.selectionMemory = {};
    // Static config exposed by Liquid for cart wiring.
    this.formId = '';
    this.pageVariantId = '';
    this.tableVariantId = '';
    // Color handle -> { sets, soldOut } for "just the table".
    this.tableAvailabilityByColor = {};
    this.currentColorHandle = '';
    this.mainIsBundle = false;
    this.currentMode = 'table';

    this.onDocClick = this.onDocClick.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.section || '';
    this.defaultBundleHandle = this.dataset.defaultBundle || '';
    this.formId = this.dataset.formId || '';
    this.pageVariantId = this.dataset.pageVariantId || '';
    this.tableVariantId = this.dataset.tableVariantId || this.pageVariantId;
    // Table price + compare-at (cents) used to restore the price anchors
    // when the customer switches back to "Just the table" from a bundle.
    this.tablePrice = Number(this.dataset.tablePrice) || 0;
    this.tableCompareAt = Number(this.dataset.tableCompareAt) || this.tablePrice;
    try {
      this.tableAvailabilityByColor = JSON.parse(
        this.dataset.tableAvailabilityByColor || '{}'
      );
    } catch (_) {
      this.tableAvailabilityByColor = {};
    }
    this.currentColorHandle = this.handleize(this.dataset.parentColor || '');
    this.mainIsBundle = this.dataset.mainIsBundle === 'true';
    this.currentMode = this.dataset.initialMode || (this.mainIsBundle ? 'bundle' : 'table');

    this.currentBundleHandle = '';

    this.readConfig();

    // In the stacked layout all present categories are visible at once —
    // activeTab is kept for internal compat but not used for display.
    this.activeTab = this.firstPresentCategory(this.defaultBundleHandle);

    this.captureInitialSelection();
    this.bindModeRadios();
    this.bindBaseRadios();
    this.bindTabs();
    this.bindRadios();
    this.bindSwatches();

    // The buy-buttons <form> and every page block BELOW the bundle
    // builder are parsed AFTER this element, so they don't exist yet
    // during the initial connectedCallback. Defer all setup that reaches
    // outside the builder (form button disabling, sibling locking, price
    // anchors) until the section has finished parsing.
    this.whenReady(() => {
      // Restore the customer's prior base choice across a variant-change
      // re-render (the element is recreated fresh, defaulting to table on a
      // table PDP). Replaying the selection re-fires the mode/bundle-change
      // events so the gallery, title, description and prices all follow.
      const saved = BB_LAST_SELECTION[this.sectionId];
      if (saved && saved.mode === 'bundle' && saved.handle && this.bundleConfig(saved.handle)) {
        this.selectBundleFromBaseList(saved.handle);
        return;
      }
      if (saved && saved.mode === 'table') {
        this.applyMode('table');
        return;
      }

      // On bundle PDPs: the default bundle is pre-checked via Liquid — just
      // call selectBundle() to wire price anchors, personalize, and form.
      // On table PDPs: "Just the table" is pre-selected in the base list;
      // do NOT auto-select a bundle — keep the builder in table mode so the
      // customer lands with the table option active, as they would expect.
      if (this.mainIsBundle && this.defaultBundleHandle) {
        this.selectBundle(this.defaultBundleHandle);
        return;
      }

      this.syncForm();
      this.updateAddToCartState();
      // Re-assert mode-scoped visibility on (re)init — a variant-change
      // re-render recreates this element at its initial mode without
      // firing `bundle-builder:mode-change`, so opted-in blocks outside
      // the builder (e.g. bundle-included-items) must be re-synced here.
      this.syncModeVisibility();

      // If we start in bundle mode (direct bundle PDP), immediately retire
      // the static server-rendered Bundle Discount rows and take over with
      // the JS-driven row. Without this call the static row stays visible
      // until the first trim switch, causing a duplicate when the user
      // picks a different trim (static old + JS new both show together).
      if (this.currentMode === 'bundle') {
        this.updateBundlePriceAnchors(this.currentBundleHandle);
      }
    });

    document.addEventListener('click', this.onDocClick);
  }

  // Run `fn` once the document has finished parsing. During the initial
  // page parse this element's connectedCallback fires before its later
  // siblings (buy-buttons form, blocks below) exist; when the element is
  // recreated later (e.g. variant change re-render) the document is
  // already complete so `fn` runs synchronously.
  whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  // Resolves the first category in catalog order that's `present` on the
  // given bundle. Used to pick a sensible default when the SSR panel
  // info isn't available (e.g. all panels hidden because the bundle has
  // no personalize categories at all — degenerate but possible).
  firstPresentCategory(bundleHandle) {
    const cfg = this.bundleConfig(bundleHandle);
    if (!cfg || !cfg.categories) return '';
    for (const [catHandle, cat] of Object.entries(cfg.categories)) {
      if (cat && cat.present) return catHandle;
    }
    return '';
  }

  // Returns the list of category handles for the current bundle, in
  // catalog order (insertion order on the parsed JSON object).
  currentCategoryHandles() {
    const cfg = this.bundleConfig(this.currentBundleHandle);
    if (!cfg || !cfg.categories) return [];
    return Object.keys(cfg.categories);
  }

  // Returns the list of category handles across ALL bundles. Used for
  // clearing stale cart form inputs in syncForm() so e.g. a chair
  // selection from a previous bundle doesn't leak into a benches-only
  // bundle.
  allCategoryHandles() {
    const set = new Set();
    this.bundlesConfig.forEach((b) => {
      if (b.categories) {
        Object.keys(b.categories).forEach((c) => set.add(c));
      }
    });
    return Array.from(set);
  }

  // Maps a catalog handle (e.g. 'chairs', 'panel-storage') to the cart
  // line property key used by the Shopify Cart Transform Function.
  //
  // The Function (extensions/bundle-builder-cart-transform/) currently
  // looks for hardcoded `_bb_chair_*` and `_bb_bench_*` properties, so
  // we map the catalog chair/bench handles to those legacy keys
  // (singular). The outdoor line reuses the same expand slots: a bundle
  // never contains both `chairs` and `outdoor-chairs` (indoor bundles use
  // `chairs`/`benches`, outdoor bundles use `outdoor-chairs`/
  // `outdoor-benches`), so collapsing them onto `chair`/`bench` is
  // collision-free and lets the Function expand outdoor items with no
  // backend change. Other categories convert kebab-case → snake_case,
  // e.g. `panel-storage` → `panel_storage`. Those property keys will
  // only be consumed in production after the Function is generalized
  // (one-line loop change). Until then, picking such a category writes
  // the property but the Function ignores it — no breakage, just no
  // cart-side expansion yet.
  categoryToPropertyKey(catHandle) {
    if (catHandle === 'chairs' || catHandle === 'outdoor-chairs') return 'chair';
    if (catHandle === 'benches' || catHandle === 'outdoor-benches') return 'bench';
    return catHandle.replace(/-/g, '_');
  }

  // -------------------------------------------------- mode radio (table/bundle)

  bindModeRadios() {
    this.querySelectorAll('input[data-bb-mode-radio]').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.applyMode(radio.value);
      });
    });
  }

  // Blocks OUTSIDE the builder can opt into bundle-mode-only visibility
  // by carrying `data-bb-mode-visible` (e.g. "Already included in your
  // Bundle"). Hidden in table mode, shown in bundle mode — the same
  // contract as the internal [data-bb-bundle-content] wrapper.
  syncModeVisibility() {
    document.querySelectorAll('[data-bb-mode-visible]').forEach((el) => {
      el.classList.toggle('hidden', this.currentMode !== 'bundle');
    });
  }

  applyMode(mode) {
    this.currentMode = mode;
    // Content wrap is always open (mode cards removed; Just the table is
    // now the first item in the base list). No open/close toggle needed.
    this.syncModeVisibility();
    this.refreshOverflowClips();

    if (mode === 'bundle') {
      if (this.currentBundleHandle) {
        this.updateBundlePriceAnchors(this.currentBundleHandle);
      } else {
        this.restoreTablePriceAnchors();
        this.setPersonalizeVisible(false);
      }
    } else {
      // Switching back to table — hide personalize, restore price anchors,
      // restore page meta, and ensure the "Just the table" base radio is checked.
      this.setPersonalizeVisible(false);
      this.restoreTablePriceAnchors();
      this.updatePageMeta(null);
      const tableRadio = this.querySelector('input[data-bb-base-radio][value="__table__"]');
      if (tableRadio && !tableRadio.checked) tableRadio.checked = true;
    }

    this.syncForm();
    this.updateAddToCartState();
    this.dispatchEvent(
      new CustomEvent('bundle-builder:mode-change', {
        bubbles: true,
        detail: { mode },
      })
    );
    this.dispatchEvent(
      new CustomEvent('bundle-builder:bundle-change', {
        bubbles: true,
        detail: {
          handle: this.currentBundleHandle,
          mode,
          config: this.bundleConfig(this.currentBundleHandle),
          selection: this.selectionMemory[this.currentBundleHandle] || {},
        },
      })
    );
  }

  // On the mobile mode-row carousel the selected card may be peeking
  // off-screen — smooth-scroll the row so the chosen card slides fully into
  // view. No-op on desktop (the grid layout doesn't scroll) and when the
  // card is already in view.
  scrollModeCardIntoView() {
    const row = this.querySelector('[data-bb-mode-row]');
    if (!row) return;
    const checked = row.querySelector('input[data-bb-mode-radio]:checked');
    const card = checked ? checked.closest('.bb-mode-card') : null;
    if (!card) return;
    // Only the mobile carousel overflows horizontally.
    if (row.scrollWidth <= row.clientWidth + 1) return;
    // Align the card to the row's start, honoring its 16px scroll-padding.
    const target = Math.max(0, card.offsetLeft - 16);
    row.scrollTo({ left: target, behavior: 'smooth' });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    if (this._overflowTimer) clearTimeout(this._overflowTimer);
    if (this.btnObserver) this.btnObserver.disconnect();
  }

  // -------------------------------------------------------------- config

  readConfig() {
    const node = this.querySelector('[data-bb-config]');
    if (!node) return;
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      this.bundlesConfig = parsed.bundles || [];
    } catch (_) {
      this.bundlesConfig = [];
    }
  }

  bundleConfig(handle) {
    return this.bundlesConfig.find((b) => b.handle === handle) || null;
  }

  // ----------------------------------------------------- initial memory

  captureInitialSelection() {
    // Snapshot whatever radio is checked + popover-active swatch per
    // catalog category present on the current bundle.
    this.currentCategoryHandles().forEach((catHandle) => {
      const checked = this.querySelector(
        `input[data-bb-radio][data-bb-tab="${catHandle}"]:checked`
      );
      if (!checked) return;
      const handle = checked.dataset.itemHandle || '';
      const activeSwatch = this.querySelector(
        `[data-bb-swatch-option="${handle}"].border-secondary`
      );
      this.rememberSelection(this.currentBundleHandle, catHandle, {
        handle,
        variantId: activeSwatch ? activeSwatch.dataset.variantId : '',
      });
    });
  }

  rememberSelection(bundleHandle, tab, value) {
    if (!bundleHandle) return;
    if (!this.selectionMemory[bundleHandle]) {
      this.selectionMemory[bundleHandle] = {};
    }
    this.selectionMemory[bundleHandle][tab] = value;
  }

  recallSelection(bundleHandle, tab) {
    return (
      (this.selectionMemory[bundleHandle] &&
        this.selectionMemory[bundleHandle][tab]) ||
      null
    );
  }

  // Returns the handle of the first bundle in the config list. Used as the
  // fallback auto-selection when no defaultBundleHandle is set (standalone-
  // table PDPs where the customer clicks "Bundle").
  firstBundleHandle() {
    return (this.bundlesConfig[0] && this.bundlesConfig[0].handle) || '';
  }

  // Auto-select the best-match (or first) radio card in each present
  // personalize category for `handle` when no prior selection is remembered.
  // Runs after restoreSelectionForBundle() so remembered picks take precedence.
  //
  // The Liquid marks the best-match card with `data-bb-best-match` (resolved
  // by product_family matching the main page product). It also uses CSS
  // `order:-1` to pull that card to the visual front, but `querySelector`
  // uses DOM order — so we must explicitly prefer `[data-bb-best-match]`
  // over the first radio in the DOM.
  autoSelectDefaults(handle) {
    const cfg = this.bundleConfig(handle);
    if (!cfg || !cfg.categories) return;
    Object.entries(cfg.categories).forEach(([catHandle, cat]) => {
      if (!cat || !cat.present) return;
      // Skip if a card is already checked (restored from memory or SSR).
      const alreadyChecked = this.querySelector(
        `input[data-bb-radio][data-bb-tab="${catHandle}"]:checked`
      );
      if (alreadyChecked) return;
      // Prefer the best-match radio (data-bb-best-match); fall back to first.
      const radio =
        this.querySelector(
          `input[data-bb-radio][data-bb-tab="${catHandle}"][data-bb-best-match]`
        ) ||
        this.querySelector(`input[data-bb-radio][data-bb-tab="${catHandle}"]`);
      if (!radio) return;
      radio.checked = true;
      const itemHandle = radio.dataset.itemHandle || '';
      // Resolve the default swatch variant (the one Liquid pre-marked
      // `.border-secondary` based on the parent page color).
      const variantId = this.resolveTabVariantId(catHandle);
      this.rememberSelection(handle, catHandle, { handle: itemHandle, variantId });
    });
  }

  // -------------------------------------------------- base radio list

  bindBaseRadios() {
    this.querySelectorAll('input[data-bb-base-radio]').forEach((radio) => {
      // Warm this bundle's (hidden, lazy-loaded) gallery images on intent —
      // hover / focus — so they're already cached by the time the click lands
      // and the gallery swap feels instant. No touchstart: on touch devices
      // that fires for plain scrolling over the card, downloading megabytes
      // of hidden images the user never asked for.
      if (radio.value && radio.value !== '__table__') {
        const card = radio.closest('label') || radio.parentElement;
        if (card) {
          const warm = () => this.prefetchGalleryFor(radio.value);
          card.addEventListener('pointerenter', warm, { once: true });
          card.addEventListener('focusin', warm, { once: true });
        }
      }

      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        if (radio.value === '__table__') {
          this.applyMode('table');
          BB_LAST_SELECTION[this.sectionId] = { mode: 'table' };
        } else {
          this.selectBundleFromBaseList(radio.value);
          BB_LAST_SELECTION[this.sectionId] = { mode: 'bundle', handle: radio.value };
        }
        // Mobile only: wait for the card selection animation (~300ms) to
        // finish, then scroll to the top so the gallery update is visible.
        if (window.innerWidth < 768) {
          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 300);
        }
      });
    });
  }

  // Flip a bundle's hidden gallery + thumbnail images from loading="lazy" to
  // eager so the browser downloads them immediately. Hidden (display:none)
  // images are skipped while lazy, so without this the first switch to a
  // bundle has to wait on a network fetch.
  //
  // Deliberately narrow: only the slides the swap will actually show — this
  // bundle in the currently selected color (mirroring switchGallerySource's
  // "same color, else first available" fallback), and only in the gallery for
  // the current viewport. Warming every color of every bundle here used to
  // download ~50MB of hidden 1250px images on page load.
  prefetchGalleryFor(handle) {
    if (!handle || handle === '__table__') return;

    const esc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : s);
    const mg = document.querySelector('media-gallery');
    const color = (mg && mg.currentOption) || '';
    const key = handle + '::' + color;
    this._prefetched = this._prefetched || new Set();
    if (this._prefetched.has(key)) return;
    this._prefetched.add(key);

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const inCurrentViewportGallery = (li) =>
      isDesktop === !li.closest('mobile-product-gallery');

    let items = color
      ? [
          ...document.querySelectorAll(
            `li[data-gallery-source="${esc(handle)}"][data-variant-option~="${esc(color)}"]`
          ),
        ].filter(inCurrentViewportGallery)
      : [];
    if (!items.length) {
      const fallback = [
        ...document.querySelectorAll(`li[data-gallery-source="${esc(handle)}"]`),
      ].filter(inCurrentViewportGallery);
      // No color match — warm only the first slide so the fallback swap still
      // paints quickly without pulling every color's media.
      items = fallback.slice(0, 1);
    }

    items.forEach((li) => {
      li.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
        // Pre-decode so the first paint after the gallery swap is instant —
        // otherwise the browser decodes the (large) image on the main thread
        // at swap time, which is the residual delay even when it's cached.
        if (typeof img.decode === 'function') {
          img.decode().catch(() => {});
        }
      });
    });
  }

  // Called when the customer picks a bundle from the unified base list.
  // Switches to bundle mode (if needed) then selects the specific bundle.
  selectBundleFromBaseList(handle) {
    this.prefetchGalleryFor(handle);
    const modeChanged = this.currentMode !== 'bundle';
    this.currentMode = 'bundle';
    const wrap = this.querySelector('[data-bb-content-wrap]');
    if (wrap) wrap.classList.add('bb-content-open');
    this.syncModeVisibility();
    this.refreshOverflowClips();
    this.selectBundle(handle);
    this.updatePageMeta(handle);
    if (modeChanged) {
      this.dispatchEvent(
        new CustomEvent('bundle-builder:mode-change', {
          bubbles: true,
          detail: { mode: 'bundle' },
        })
      );
    }
  }

  // Toggle the "Personalize your Bundle" block. Hidden until a base is
  // picked; shown once one is.
  setPersonalizeVisible(visible) {
    const el = this.querySelector('[data-bb-personalize]');
    // bb-personalize-open drives a CSS-Grid slide-down (0fr → 1fr); see the
    // <style> block in upsell-bundle-builder.liquid.
    if (el) el.classList.toggle('bb-personalize-open', visible);
    this.refreshOverflowClips();
  }

  // The bundle content and personalize sections use overflow:hidden so their
  // expand/collapse (and height-tween) animations clip cleanly. That same
  // clip cuts off descendant popovers (the color-swatch picker). Resolve the
  // conflict by clipping only while collapsed or animating, and switching to
  // overflow:visible once the section is open and settled.
  //
  // Strategy: drop the visible flag immediately (so any in-progress animation
  // clips), then re-apply it after the animation window if the section is
  // still open. Called from every entry point that opens/closes/tweens these
  // regions.
  refreshOverflowClips() {
    const content = this.querySelector('[data-bb-bundle-content]');
    const inner = this.querySelector('[data-bb-personalize-inner]');
    if (content) content.classList.remove('bb-overflow-visible');
    if (inner) inner.classList.remove('bb-overflow-visible');

    if (this._overflowTimer) clearTimeout(this._overflowTimer);
    this._overflowTimer = setTimeout(() => {
      const wrap = this.querySelector('[data-bb-content-wrap]');
      if (content && wrap && wrap.classList.contains('bb-content-open')) {
        content.classList.add('bb-overflow-visible');
      }
      const personalize = this.querySelector('[data-bb-personalize]');
      if (
        inner &&
        personalize &&
        personalize.classList.contains('bb-personalize-open')
      ) {
        inner.classList.add('bb-overflow-visible');
      }
    }, 500);
  }

  // Smoothly tween every height-variable region across a bundle switch and
  // replay a fade/slide-in on the panels that end up visible. `applyChanges`
  // performs the actual DOM mutation (mode-card items swap, panel show/hide,
  // selection restore, step gating) between the before/after measurements.
  //
  // Two regions change height when switching bundles:
  //   - [data-bb-personalize-inner]  the personalize steps (chairs/benches…)
  //   - [data-bb-mode-items-wrap]    the "Bundle" mode card's included-items
  //                                  list (gains/loses a line, e.g. Chairs)
  //
  // The personalize area is only tweened when it's already open — on the
  // first base pick the CSS-Grid reveal animation owns that transition.
  animatePersonalizeResize(applyChanges) {
    const personalize = this.querySelector('[data-bb-personalize]');
    const personalizeOpen =
      personalize && personalize.classList.contains('bb-personalize-open');

    const targets = [];
    const itemsWrap = this.querySelector('[data-bb-mode-items-wrap]');
    if (itemsWrap) targets.push(itemsWrap);
    const inner = this.querySelector('[data-bb-personalize-inner]');
    if (inner && personalizeOpen) targets.push(inner);

    // Cancel any tweens still in flight so measurements are accurate.
    if (this._resizeCleanups) this._resizeCleanups.forEach((fn) => fn());

    const starts = targets.map((el) => el.offsetHeight);
    applyChanges();
    this.replayPanelIntro();

    this._resizeCleanups = [];
    targets.forEach((el, i) => this.tweenHeight(el, starts[i]));
  }

  // Animate `el` from a previously-measured `startH` to its current natural
  // height. No-op when the height is unchanged. Self-cleans on transitionend
  // (with a timeout fallback) and restores the element to auto height.
  tweenHeight(el, startH) {
    const endH = el.offsetHeight;
    if (startH === endH) return;

    el.style.height = `${startH}px`;
    el.classList.add('bb-resizing');
    // Force reflow so the start height is committed before transitioning.
    void el.offsetHeight;
    el.style.height = `${endH}px`;

    let timer = null;
    const cleanup = () => {
      el.classList.remove('bb-resizing');
      el.style.height = '';
      el.removeEventListener('transitionend', onEnd);
      if (timer) clearTimeout(timer);
    };
    const onEnd = (e) => {
      if (e.target === el && e.propertyName === 'height') cleanup();
    };
    el.addEventListener('transitionend', onEnd);
    // Fallback in case transitionend doesn't fire (e.g. interrupted).
    timer = setTimeout(cleanup, 460);
    this._resizeCleanups.push(cleanup);
  }

  // Re-trigger the fade/slide-in keyframe on every currently-visible panel.
  replayPanelIntro() {
    this.querySelectorAll('[data-bb-panel]').forEach((panel) => {
      if (panel.classList.contains('hidden')) return;
      panel.classList.remove('bb-panel-animate');
      // Force reflow so re-adding the class restarts the animation.
      void panel.offsetWidth;
      panel.classList.add('bb-panel-animate');
    });
  }

  // Document-level click to dismiss any open swatch popover when clicking
  // outside the block.
  onDocClick(e) {
    if (!this.contains(e.target)) {
      this.closeAllSwatchPopovers();
    }
  }

  // ---------------------------------------------------- bundle switching

  selectBundle(handle) {
    if (!handle || handle === this.currentBundleHandle) return;

    // Save what's currently chosen on each category tab into memory
    // under the OUTGOING bundle so we can restore it on return.
    this.currentCategoryHandles().forEach((catHandle) => {
      const checked = this.querySelector(
        `input[data-bb-radio][data-bb-tab="${catHandle}"]:checked`
      );
      if (!checked) return;
      const itemHandle = checked.dataset.itemHandle || '';
      const wrap = this.querySelector(
        `[data-bb-swatch-popover="${itemHandle}"]`
      );
      let variantId = '';
      if (wrap) {
        const active = wrap.querySelector('.border-secondary');
        if (active) variantId = active.dataset.variantId || '';
      }
      this.rememberSelection(this.currentBundleHandle, catHandle, {
        handle: itemHandle,
        variantId,
      });
    });

    // Keep the base radio in sync (covers programmatic selection, e.g.
    // restore). User-driven picks already have the radio checked.
    const baseRadio = this.querySelector(
      `input[data-bb-base-radio][value="${handle}"]`
    );
    if (baseRadio && !baseRadio.checked) baseRadio.checked = true;

    this.currentBundleHandle = handle;
    // A base is now chosen — reveal + gate the personalize steps and
    // re-lock everything below until those steps are completed. Wrap every
    // height-changing mutation (mode-card items swap + personalize step
    // visibility) in a height tween so switching between bundles with a
    // different number of included items / category steps slides smoothly
    // instead of jumping. Measurements happen around this callback, so the
    // mode-card swaps must run INSIDE it.
    this.animatePersonalizeResize(() => {
      // Swap the Bundle mode-card image, included-items list, and uplift /
      // savings meta to match the newly-selected trim.
      this.querySelectorAll('[data-bb-mode-bundle-image]').forEach((el) => {
        el.classList.toggle('is-visible', el.dataset.bbModeBundleImage === handle);
      });
      this.querySelectorAll('[data-bb-mode-bundle-items]').forEach((el) => {
        el.classList.toggle('hidden', el.dataset.bbModeBundleItems !== handle);
      });
      this.querySelectorAll('[data-bb-mode-bundle-meta]').forEach((el) => {
        el.classList.toggle('hidden', el.dataset.bbModeBundleMeta !== handle);
      });

      this.applyBundleTabVisibility(handle);
      this.restoreSelectionForBundle(handle);
      this.autoSelectDefaults(handle);
    });
    this.syncForm();
    this.updateAddToCartState();
    this.updateBundlePriceAnchors(handle);

    this.dispatchEvent(
      new CustomEvent('bundle-builder:bundle-change', {
        bubbles: true,
        detail: {
          handle,
          config: this.bundleConfig(handle),
          selection: this.selectionMemory[handle] || {},
        },
      })
    );
  }

  // Re-point the `data-amount` anchors used by bundle-item.js so the
  // subtotal + sticky bar + compare-at strikethrough recompute against
  // the newly-selected trim. bundle-item.js listens for
  // `bundle-builder:bundle-change` (dispatched right after this call)
  // and calls its own `updateBundlePrice()` which reads these anchors,
  // adds the current add-on totals, and re-renders the visible numbers.
  // Add-on quantities are preserved (the stepper inputs aren't touched).
  //
  // Per finance: the bundle is sold at a flat price regardless of which
  // chair/bench/sideboard the customer picks in the personalize tabs,
  // so this function intentionally does NOT apply a per-radio delta.
  // Only the trim's own configured price + compare-at drive the visible
  // numbers. If that decision flips back, see the Phase 1 commit for
  // the delta math that needs to come back.
  updateBundlePriceAnchors(handle) {
    const config = this.bundleConfig(handle);
    if (!config) return;
    const basePrice = Number(config.price);
    if (!Number.isFinite(basePrice)) return;
    const baseCompareAt = Number(config.compareAtPrice) || basePrice;

    const subtotal = document.getElementById('Bundle-Savings-Subtotal');
    if (subtotal) {
      subtotal.dataset.amount = String(basePrice);
      if (subtotal.dataset.amountForCoupon !== undefined) {
        subtotal.dataset.amountForCoupon = String(basePrice);
      }
    }

    const compareEls = [
      ...document.querySelectorAll('#Bundle-Compare-At-price'),
      ...document.querySelectorAll('.sticky-menu-compare-at-price'),
    ];
    compareEls.forEach((el) => {
      el.dataset.amount = String(baseCompareAt);
    });

    // Bundle mode now has a discount (MSRP − price) — surface the
    // "Bundle Discount" row in the savings breakdown.
    this.setBundleDiscountRow(baseCompareAt - basePrice);

    // With an MSRP showing in the sticky bar, hide the "or $X/mo"
    // financing line (matches the at-load compare-at layout).
    this.setStickyFinancingVisible(false);
  }

  // Restore the subtotal / sticky-bar / financing anchors to the
  // standalone table variant's price + compare-at (MSRP). Mirror of
  // updateBundlePriceAnchors() for "Just the table" mode.
  restoreTablePriceAnchors() {
    if (!this.tablePrice) return;

    const subtotal = document.getElementById('Bundle-Savings-Subtotal');
    if (subtotal) {
      subtotal.dataset.amount = String(this.tablePrice);
      if (subtotal.dataset.amountForCoupon !== undefined) {
        subtotal.dataset.amountForCoupon = String(this.tablePrice);
      }
    }

    const compareEls = [
      ...document.querySelectorAll('#Bundle-Compare-At-price'),
      ...document.querySelectorAll('.sticky-menu-compare-at-price'),
    ];
    compareEls.forEach((el) => {
      el.dataset.amount = String(this.tableCompareAt || this.tablePrice);
    });

    // "Just the table" — no bundle discount, hide the row.
    this.setBundleDiscountRow(0);

    // Restore the sticky "or $X/mo" financing line (only matters for
    // pages whose page product has no compare-at at load).
    this.setStickyFinancingVisible(true);
  }

  // Toggle the sticky bar's "or $X/mo" financing line. Only affects the
  // else-branch markup in sticky-pdp-menu.liquid (page product without a
  // compare-at); pages that ship with an MSRP never render this line.
  setStickyFinancingVisible(visible) {
    document
      .querySelectorAll('.sticky-menu-financing-price')
      .forEach((el) => {
        el.classList.toggle('hidden', !visible);
      });
  }

  // Show / hide + populate the JS-driven "Bundle Discount" row
  // (#Bundle-Discount-Row in bundle_savings.liquid). Amount in cents.
  setBundleDiscountRow(amountCents) {
    // The JS row is now the single source of truth — retire the static
    // server-rendered row (the initial trim's metafield discount) so we
    // don't end up with the old trim's line plus the new trim's line.
    document
      .querySelectorAll('[data-static-bundle-discount]')
      .forEach((el) => el.classList.add('hidden'));

    const rows = [
      ...document.querySelectorAll('#Bundle-Discount-Row'),
      ...document.querySelectorAll('[id^="current-bundle-discount-"]'),
    ];
    rows.forEach((row) => {
      if (amountCents > 0) {
        const amtEl =
          row.querySelector('[data-bb-bundle-discount-amount]') ||
          row.querySelector('span:last-child');
        if (amtEl) amtEl.textContent = '-' + this.formatMoney(amountCents);
        row.classList.remove('hidden');
      } else {
        row.classList.add('hidden');
      }
    });
  }

  formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, Shopify.money_format);
    }
    return '$' + Math.round(cents / 100).toLocaleString();
  }

  applyBundleTabVisibility(handle) {
    const config = this.bundleConfig(handle);
    if (!config || !config.categories) return;

    // Stacked layout: show each category section when present for this
    // bundle, hide it when not. No tab switching needed.
    let anyPresent = false;
    Object.entries(config.categories).forEach(([catHandle, cat]) => {
      const isPresent = !!(cat && cat.present);
      if (isPresent) anyPresent = true;
      const panel = this.querySelector(`[data-bb-panel="${catHandle}"]`);
      if (panel) panel.classList.toggle('hidden', !isPresent);
    });

    if (!anyPresent) {
      this.querySelectorAll('[data-bb-panel]').forEach((p) =>
        p.classList.add('hidden')
      );
    }
  }

  restoreSelectionForBundle(handle) {
    this.currentCategoryHandles().forEach((catHandle) => {
      const remembered = this.recallSelection(handle, catHandle);
      if (!remembered || !remembered.handle) return;
      const radio = this.querySelector(
        `input[data-bb-radio][data-bb-tab="${catHandle}"][data-item-handle="${remembered.handle}"]`
      );
      if (radio) radio.checked = true;

      if (remembered.variantId) {
        const swatch = this.querySelector(
          `[data-bb-swatch-option="${remembered.handle}"][data-variant-id="${remembered.variantId}"]`
        );
        if (swatch) this.applySwatchPick(remembered.handle, swatch);
      }
    });
  }

  // ----------------------------------------- see-more reset helper

  // Collapses the description back to the truncated view instantly (no
  // animation) — called whenever content is swapped so stale expanded state
  // from a previous bundle doesn't carry over to the new one.
  _resetSeeMore(descEl) {
    if (!descEl) return;
    const truncated = descEl.querySelector('.description-truncated');
    const full = descEl.querySelector('.description-full');
    if (truncated) truncated.classList.remove('hidden');
    if (full) full.classList.add('hidden');
    // Release any pinned height from a previous animation
    descEl.style.height = 'auto';
    descEl.style.transition = '';
  }

  // ------------------------------------------ page title + description swap

  // Updates the page-level <h1> and product description to match the selected
  // bundle. When handle is null/undefined the original (table) content is
  // restored. The description container's inner HTML is swapped so rich-text
  // content (lists, formatting) is preserved.
  updatePageMeta(handle) {
    const titleEl = document.querySelector('[data-bb-product-title]');
    const descEl = document.querySelector('[data-bb-product-description]');
    const els = [titleEl, descEl].filter(Boolean);

    const applyContent = () => {
      if (handle) {
        const config = this.bundleConfig(handle);
        if (!config) return;

        if (titleEl && config.productTitle) {
          titleEl.textContent = config.productTitle;
        }
        if (descEl && config.descriptionHtml) {
          const clamp = descEl.querySelector('.line-clamp-2');
          const full = descEl.querySelector('.description-full');

          // Save originals the first time (inner HTML of the sub-divs only,
          // not the whole container — this keeps the event listener nodes alive).
          if (clamp && !clamp.dataset.bbOriginalHtml) {
            clamp.dataset.bbOriginalHtml = clamp.innerHTML;
          }
          if (full && !full.dataset.bbOriginalHtml) {
            const btn = full.querySelector('.see-more-button');
            // Store just the text/content before the button
            full.dataset.bbOriginalHtml = btn ? full.innerHTML.replace(btn.outerHTML, '') : full.innerHTML;
          }

          if (clamp) clamp.innerHTML = config.descriptionHtml;
          if (full) {
            const btn = full.querySelector('.see-more-button');
            full.innerHTML = config.descriptionHtml;
            if (btn) full.appendChild(btn);
          }
          // Always reset to truncated view when swapping descriptions
          this._resetSeeMore(descEl);
        }
      } else {
        if (titleEl) {
          titleEl.textContent = titleEl.dataset.bbOriginalTitle || titleEl.textContent;
        }
        if (descEl) {
          // Restore inner content only — never replace the container so that
          // the see-more event listeners (bound to the sub-div nodes) survive.
          const clamp = descEl.querySelector('.line-clamp-2');
          const full = descEl.querySelector('.description-full');
          if (clamp && clamp.dataset.bbOriginalHtml !== undefined) {
            clamp.innerHTML = clamp.dataset.bbOriginalHtml;
            delete clamp.dataset.bbOriginalHtml;
          }
          if (full && full.dataset.bbOriginalHtml !== undefined) {
            const btn = full.querySelector('.see-more-button');
            full.innerHTML = full.dataset.bbOriginalHtml;
            if (btn) full.appendChild(btn);
            delete full.dataset.bbOriginalHtml;
          }
          // Reset to truncated view
          this._resetSeeMore(descEl);
        }
      }
    };

    // Fade out → swap → fade in
    els.forEach((el) => el.classList.add('bb-meta-changing'));
    // Wait for the fade-out transition (200ms), swap, then fade in
    setTimeout(() => {
      applyContent();
      els.forEach((el) => el.classList.remove('bb-meta-changing'));
    }, 200);
  }

  // ---------------------------------------------------------------- tabs
  // No tab buttons in stacked layout — categories are all visible at once.

  bindTabs() {}

  setActiveTab(tab) {
    this.activeTab = tab;
  }

  // ----------------------------------------------------------- radios

  bindRadios() {
    this.querySelectorAll('input[data-bb-radio]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const tab = radio.dataset.bbTab || '';
        const handle = radio.dataset.itemHandle || '';
        this.rememberSelection(this.currentBundleHandle, tab, {
          handle,
          variantId: this.activeVariantIdFor(handle),
        });
        // Personalize-tab radio picks update the cart payload (so the
        // Cart Transform Function expands the bundle with the right
        // chair/bench variant) but DO NOT trigger a price recompute —
        // the bundle is flat-priced regardless of personalize choice.
        this.syncForm();
        this.updateAddToCartState();
        this.dispatchEvent(
          new CustomEvent('bundle-builder:radio-change', {
            bubbles: true,
            detail: { tab, handle, bundle: this.currentBundleHandle },
          })
        );
      });
    });
  }

  activeVariantIdFor(itemHandle) {
    const wrap = this.querySelector(
      `[data-bb-swatch-popover="${itemHandle}"]`
    );
    if (!wrap) return '';
    const active = wrap.querySelector('.border-secondary');
    return active ? active.dataset.variantId || '' : '';
  }

  // --------------------------------------------------------- swatches

  bindSwatches() {
    // Triggers (per card).
    this.querySelectorAll('[data-bb-swatch-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = trigger.dataset.bbSwatchTrigger || '';
        this.toggleSwatchPopover(handle);
      });
    });

    // Delegated click handler — handles BOTH swatch picks (inside the
    // popover) and direct card clicks (anywhere on the card body that
    // isn't the swatch trigger / popover).
    this.addEventListener('click', (e) => {
      // 1. Swatch pick inside a popover.
      const opt = e.target.closest('[data-bb-swatch-option]');
      if (opt && this.contains(opt)) {
        e.preventDefault();
        e.stopPropagation();
        const handle = opt.dataset.bbSwatchOption || '';
        this.applySwatchPick(handle, opt);
        const radio = this.querySelector(
          `input[data-bb-radio][data-item-handle="${handle}"]`
        );
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.closeSwatchPopover(handle);
        return;
      }

      // 2. Card click → tick the card's radio. Skip if the click was
      //    on (or inside) the swatch trigger or the swatch popover —
      //    those have their own handlers that shouldn't double-fire
      //    radio selection.
      const card = e.target.closest('label.bb-option');
      if (!card || !this.contains(card)) return;
      if (e.target.closest('[data-bb-swatch-trigger]')) return;
      if (e.target.closest('[data-bb-swatch-popover-wrap]')) return;
      const radio = card.querySelector('input[data-bb-radio]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  toggleSwatchPopover(handle) {
    const popover = this.querySelector(
      `[data-bb-swatch-popover-wrap="${handle}"]`
    );
    if (!popover) return;
    const isOpen = popover.classList.contains('is-open');
    this.closeAllSwatchPopovers();
    if (!isOpen) {
      popover.classList.add('is-open');
      const trigger = this.querySelector(
        `[data-bb-swatch-trigger="${handle}"]`
      );
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeSwatchPopover(handle) {
    const popover = this.querySelector(
      `[data-bb-swatch-popover-wrap="${handle}"]`
    );
    if (popover) popover.classList.remove('is-open');
    const trigger = this.querySelector(
      `[data-bb-swatch-trigger="${handle}"]`
    );
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  closeAllSwatchPopovers() {
    this.querySelectorAll('[data-bb-swatch-popover-wrap]').forEach((p) => {
      p.classList.remove('is-open');
    });
    this.querySelectorAll('[data-bb-swatch-trigger]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
    });
  }

  applySwatchPick(handle, optionEl) {
    // 1. Update the popover's active ring.
    const popover = this.querySelector(
      `[data-bb-swatch-popover="${handle}"]`
    );
    if (popover) {
      popover
        .querySelectorAll('[data-bb-swatch-option]')
        .forEach((b) => {
          b.classList.remove('border-secondary');
          b.classList.add('border-transparent');
        });
      optionEl.classList.remove('border-transparent');
      optionEl.classList.add('border-secondary');
    }

    // 2. Swap the card image: show the <li> matching the picked variant.
    const variantId = optionEl.dataset.variantId || '';
    const imageWrap = this.querySelector(
      `[data-bb-card-image="${handle}"]`
    );
    if (imageWrap && variantId) {
      imageWrap.querySelectorAll('li').forEach((li) => {
        li.classList.toggle(
          'hidden',
          li.id !== `BB_Card_Variant_Image--${variantId}`
        );
      });
    }

    // 2b. Swap the visible swatch chip on the trigger to the picked
    //     variant (add-ons-style selector shows the current color).
    const swatchWrap = this.querySelector(
      `[data-bb-card-swatch="${handle}"]`
    );
    if (swatchWrap && variantId) {
      swatchWrap
        .querySelectorAll('span[id^="BB_Card_Variant_Swatch--"]')
        .forEach((chip) => {
          chip.classList.toggle(
            'hidden',
            chip.id !== `BB_Card_Variant_Swatch--${variantId}`
          );
        });
    }

    // 3. Persist to memory for the current bundle / current radio's tab.
    const radio = this.querySelector(
      `input[data-bb-radio][data-item-handle="${handle}"]`
    );
    if (radio) {
      const tab = radio.dataset.bbTab || '';
      this.rememberSelection(this.currentBundleHandle, tab, {
        handle,
        variantId,
      });
    }

    // 4. Re-sync the cart form so the next add-to-cart picks up the
    //    newly-chosen chair / bench variant id without waiting for the
    //    radio change event (which only fires on radio toggle, not on
    //    swatch re-pick of the already-checked card).
    this.syncForm();
  }

  // -------------------------------------------------- cart form sync

  // Resolves the chair / bench variant id the customer is "carrying"
  // for a given tab in the current bundle.
  //   1. If they explicitly chose a swatch → use that.
  //   2. Otherwise fall back to whichever swatch the snippet rendered
  //      with the active ring (the parent-color best-match default).
  //   3. Last resort: the first swatch in the popover.
  resolveTabVariantId(tab) {
    const checked = this.querySelector(
      `input[data-bb-radio][data-bb-tab="${tab}"]:checked`
    );
    if (!checked) return '';
    const handle = checked.dataset.itemHandle || '';
    if (!handle) return '';

    const popover = this.querySelector(
      `[data-bb-swatch-popover="${handle}"]`
    );
    if (!popover) return '';

    const active = popover.querySelector('.border-secondary');
    if (active && active.dataset.variantId) return active.dataset.variantId;

    const first = popover.querySelector('[data-bb-swatch-option]');
    return first ? first.dataset.variantId || '' : '';
  }

  resolveTabHandle(tab) {
    const checked = this.querySelector(
      `input[data-bb-radio][data-bb-tab="${tab}"]:checked`
    );
    return checked ? checked.dataset.itemHandle || '' : '';
  }

  // Locate the host product form. We attach hidden inputs to it so the
  // existing <product-form> submit path (assets/product-form.js) carries
  // our line attributes through to /cart/add.js untouched.
  getForm() {
    if (!this.formId) return null;
    return document.getElementById(this.formId);
  }

  // Idempotent hidden-input writer. Creates the input if missing,
  // updates value if present, removes if value is empty. Keyed on
  // `name` so re-calls don't pile up duplicates.
  setFormInput(form, name, value) {
    if (!form) return;
    let input = form.querySelector(
      `input[type="hidden"][data-bb-prop="${name}"]`
    );
    if (value === '' || value == null) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.bbProp = name;
      form.appendChild(input);
    }
    input.value = String(value);
  }

  // Read the canonical form `name=id` input so we can swap it between
  // the page variant (table mode) and the bundle shell variant
  // (bundle mode) without re-rendering anything.
  setFormVariantId(form, id) {
    if (!form || !id) return;
    const input = form.querySelector('input[name="id"]');
    if (input) input.value = String(id);
  }

  // The single source of truth for what's about to be added to cart.
  // Called on connect, mode change, bundle change, radio change, and
  // swatch pick. Cheap to run — just touches hidden inputs.
  syncForm() {
    const form = this.getForm();
    if (!form) return;

    // Clear ALL `_bb_*` keys we might ever stamp before re-writing. The
    // category list is derived from the shop catalog (via the data-bb-
    // config we built in Liquid) so retiring a category from the
    // catalog automatically removes its inputs on the next render.
    const fixedKeys = [
      'properties[_bb_dynamic_bundle]',
      'properties[_bb_trim]',
      'properties[_bb_table_variant_id]',
    ];
    fixedKeys.forEach((k) => this.setFormInput(form, k, ''));

    this.allCategoryHandles().forEach((catHandle) => {
      const propKey = this.categoryToPropertyKey(catHandle);
      this.setFormInput(form, `properties[_bb_${propKey}_variant_id]`, '');
      this.setFormInput(form, `properties[_bb_${propKey}_qty]`, '');
    });

    if (this.currentMode !== 'bundle') {
      // "Just the table" — restore the page variant id, submit nothing
      // else. Cart Transform Function sees no `_bb_*` attrs and ignores.
      if (this.pageVariantId) {
        this.setFormVariantId(form, this.pageVariantId);
      }
      return;
    }

    // Bundle mode.
    const config = this.bundleConfig(this.currentBundleHandle);
    if (!config) {
      // Bundle mode but no base picked yet — fall back to the page
      // variant so an accidental add-to-cart adds the plain table, not a
      // half-configured bundle. No `_bb_*` attrs are stamped (cleared
      // above), so the Cart Transform Function leaves it alone.
      if (this.pageVariantId) this.setFormVariantId(form, this.pageVariantId);
      return;
    }

    // Shell variant id = the trim's product variant for the current
    // parent color (resolved in Liquid, baked into data-bb-config).
    if (config.variantId) {
      this.setFormVariantId(form, config.variantId);
    }

    this.setFormInput(form, 'properties[_bb_dynamic_bundle]', 'true');
    this.setFormInput(form, 'properties[_bb_trim]', config.title || this.currentBundleHandle);
    this.setFormInput(form, 'properties[_bb_table_variant_id]', this.tableVariantId || '');

    // Emit one `_bb_<key>_variant_id` + `_bb_<key>_qty` pair per
    // present category. See categoryToPropertyKey() for the catalog
    // handle → property key mapping.
    Object.entries(config.categories || {}).forEach(([catHandle, cat]) => {
      if (!cat || !cat.present || !(cat.quantity > 0)) return;
      const propKey = this.categoryToPropertyKey(catHandle);
      const variantId = this.resolveTabVariantId(catHandle);
      this.setFormInput(form, `properties[_bb_${propKey}_variant_id]`, variantId);
      this.setFormInput(form, `properties[_bb_${propKey}_qty]`, cat.quantity);
    });
  }

  // Ensure any `data-bb-incomplete` state left over from a previous render
  // is cleared — no step gating means add-to-cart is always available.
  updateAddToCartState() {
    const form = this.getForm();
    const buttons = [];
    if (form) {
      form
        .querySelectorAll('button[name="add"], .product-form__submit')
        .forEach((b) => buttons.push(b));
    }
    document.querySelectorAll('.sticky-pdp-btn').forEach((b) => buttons.push(b));
    buttons.forEach((btn) => {
      if (btn.dataset.bbIncomplete === 'true') {
        delete btn.dataset.bbIncomplete;
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });
    this.syncSoldOutState();
  }

  handleize(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  // colour handle -> { sets, soldOut } map for the currently selected bundle
  // (or "just the table").
  availabilityMapForCurrentSelection() {
    if (this.currentMode === 'bundle' && this.currentBundleHandle) {
      const config = this.bundleConfig(this.currentBundleHandle);
      return (config && config.availabilityByColor) || {};
    }
    return this.tableAvailabilityByColor || {};
  }

  // Stock for the currently selected bundle (or "just the table") + colour.
  // soldOut comes from component availability; sets === 0 is also OOS.
  availabilityForCurrentSelection() {
    const color = this.currentColorHandle;
    const byColor = this.availabilityMapForCurrentSelection();
    const entry = color ? byColor[color] : null;
    if (!entry) return { soldOut: false, sets: null };
    const sets = entry.sets;
    return {
      sets,
      soldOut: Boolean(entry.soldOut) || sets === 0,
    };
  }

  // Keep ATC, sticky ATC, gallery overlay, shipping estimator, and the colour
  // swatches in sync with the selected bundle+colour. Called on bundle switch,
  // mode switch, colour change, and after renderProductInfo() swaps
  // page-product markup.
  syncSoldOutState() {
    const { soldOut } = this.availabilityForCurrentSelection();
    this.applySoldOutToButtons(soldOut);
    this.applySoldOutOverlay(soldOut);
    this.applySoldOutToShipping(soldOut);
    this.applySoldOutToSwatches();
  }

  // The PDP colour swatches (variant-swatch buttons + the Finish Swatch block)
  // show sold-out as dimmed + struck via CSS keyed on `button[data-quant="0"]`
  // (src/css/_product.css / tailwind.css). Liquid bakes `data-quant` from the
  // PAGE product's progress_bar_sets metafield, so switching bundle bases
  // never refreshed it. Rewrite each swatch's data-quant from the selected
  // bundle's per-colour availability; restore the baked value (snapshotted in
  // data-bb-base-quant) for colours the current selection has no entry for.
  applySoldOutToSwatches() {
    const byColor = this.availabilityMapForCurrentSelection();
    document
      .querySelectorAll('button.progress_quantity')
      .forEach((btn) => {
        const color =
          btn.getAttribute('data-variant-color') ||
          btn.getAttribute('data-handle-soldout-overlay');
        if (!color) return;
        if (btn.dataset.bbBaseQuant === undefined) {
          btn.dataset.bbBaseQuant = btn.getAttribute('data-quant') || '';
        }
        const entry = byColor[color];
        if (!entry) {
          btn.setAttribute('data-quant', btn.dataset.bbBaseQuant);
          return;
        }
        const soldOut = Boolean(entry.soldOut) || entry.sets === 0;
        let quant;
        if (soldOut) {
          quant = '0';
        } else if (typeof entry.sets === 'number' && entry.sets > 0) {
          quant = String(entry.sets);
        } else {
          // In stock but no numeric sets — keep the baked value unless it
          // would wrongly dim the swatch.
          quant =
            btn.dataset.bbBaseQuant && btn.dataset.bbBaseQuant !== '0'
              ? btn.dataset.bbBaseQuant
              : '1';
        }
        btn.setAttribute('data-quant', quant);
      });
  }

  applySoldOutToButtons(soldOut) {
    const form = this.getForm();
    const buttons = [];
    if (form) {
      form
        .querySelectorAll('button[name="add"], .product-form__submit')
        .forEach((b) => buttons.push(b));
    }
    document.querySelectorAll('.sticky-pdp-btn').forEach((b) => buttons.push(b));
    if (!buttons.length) return;

    const freeGiftMessage = document.getElementById('freeGiftMessage');
    const freeGiftButtons = document.querySelectorAll('.FreeProductButton');
    const selectedFreeGift = document.querySelector('.FreeProductButton:checked');
    const shouldDisableForFreeGift =
      Boolean(freeGiftMessage) &&
      freeGiftButtons.length > 0 &&
      !selectedFreeGift;

    buttons.forEach((btn) => {
      const soldOutText =
        btn.getAttribute('data-sold-out-text') ||
        (window.variantStrings && window.variantStrings.soldOut) ||
        'Sold Out';
      const addText =
        btn.getAttribute('data-add_to_cart') ||
        (window.variantStrings && window.variantStrings.addToCart) ||
        'Add to Cart';
      const labelEl = btn.querySelector('span');
      btn.dataset.componentUnavailable = soldOut ? 'true' : 'false';
      if (soldOut) {
        btn.disabled = true;
        btn.setAttribute('disabled', 'disabled');
        if (labelEl) labelEl.textContent = soldOutText;
        else btn.textContent = soldOutText;
      } else {
        if (labelEl) labelEl.textContent = addText;
        else btn.textContent = addText;
        if (shouldDisableForFreeGift) {
          btn.disabled = true;
          btn.setAttribute('disabled', 'disabled');
        } else {
          btn.disabled = false;
          btn.removeAttribute('disabled');
        }
      }
    });
  }

  applySoldOutOverlay(soldOut) {
    const colorHandle = this.currentColorHandle;
    const label =
      (window.variantStrings && window.variantStrings.soldOut) || 'Sold Out';

    const lis = document.querySelectorAll(
      '#image-gallery li[data-variant-option], [data-mpg-slide][data-variant-option]'
    );

    lis.forEach((li) => {
      const matches =
        Boolean(colorHandle) &&
        (li.getAttribute('data-variant-option') || '')
          .split(/\s+/)
          .includes(colorHandle);

      const hosts = [
        ...li.querySelectorAll('.image-gallery-item'),
        ...li.querySelectorAll('.mpg-zoom-trigger > span.relative'),
      ];

      hosts.forEach((host) => {
        const existing = host.querySelector(':scope > .show-image-overlay');
        if (!soldOut || !matches) {
          if (existing) existing.remove();
          return;
        }
        if (existing) return;
        const overlay = document.createElement('div');
        overlay.className =
          'show-image-overlay absolute top-0 left-0 w-full h-full opacity-75 bg-black flex justify-center items-center';
        const heading = document.createElement('h2');
        heading.className =
          'inline mx-auto bg-[#cf4545] text-white px-6 py-3';
        heading.textContent = label;
        overlay.appendChild(heading);
        host.insertAdjacentElement('afterbegin', overlay);
      });
    });
  }

  applySoldOutToShipping(soldOut) {
    if (!this.sectionId) return;
    const estimator = document.getElementById(
      `shipping-estimator-${this.sectionId}`
    );
    if (estimator) estimator.classList.toggle('hidden', soldOut);
  }

  // ---------------------------------------------------------------------------
  // Base-card thumbnail crossfade on main color change.
  //
  // Each base card renders every variant as an absolutely-stacked <li> layer
  // (see snippets/upsell-bundle-builder.liquid → [data-bb-base-image]). Only
  // the parent-color match is visible; the rest are lazy-loaded and hidden.
  // On a `variant:swatch` (main product color change) we crossfade the current
  // layer out and the matching layer in — no network fetch or decode at swap
  // time, so the transition is instant and smooth. The full section still
  // re-renders shortly after (main.js → updateDom) to reconcile prices,
  // Save %, availability and the cart variant ids; that re-render lands on the
  // same color, so it is visually seamless on top of the crossfade.
  // ---------------------------------------------------------------------------
  syncBaseImagesToColor(colorHandle) {
    if (!colorHandle) return;
    this.currentColorHandle = colorHandle;
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('[data-bb-base-image]').forEach((wrap) => {
      const lis = Array.from(wrap.querySelectorAll('li[data-variant-color]'));
      if (!lis.length) return;
      let incoming = lis.find((li) => li.dataset.variantColor === colorHandle);
      // Fall back to the first layer when this color has no image for the card.
      if (!incoming) incoming = lis[0];
      const outgoing = lis.find((li) => !li.classList.contains('hidden'));
      if (outgoing === incoming) return;
      if (reduceMotion) {
        lis.forEach((li) => li.classList.toggle('hidden', li !== incoming));
        return;
      }
      this.crossfadeBaseImage(lis, outgoing, incoming);
    });
  }

  crossfadeBaseImage(lis, outgoing, incoming) {
    const DURATION = 320;
    const ease = `opacity ${DURATION}ms ease, transform ${DURATION}ms ease`;
    // Any stray visible copies (besides the two principals) drop out instantly
    // so a rapid click sequence can never leave ghost layers stacked up.
    lis.forEach((li) => {
      if (li !== outgoing && li !== incoming) {
        li.classList.add('hidden');
        li.style.cssText = '';
      }
    });
    // Reveal the incoming copy underneath, commit its starting state with a
    // forced reflow, then fade/zoom it in on the next frame.
    incoming.classList.remove('hidden');
    incoming.style.transition = 'none';
    incoming.style.opacity = '0';
    incoming.style.transform = 'scale(1.04)';
    void incoming.offsetWidth; // commit the starting state (0 → 1 transition)
    incoming.style.transition = ease;
    incoming.style.opacity = '1';
    incoming.style.transform = 'scale(1)';
    const clearIncoming = () => {
      incoming.style.transition = '';
      incoming.style.opacity = '';
      incoming.style.transform = '';
    };
    if (outgoing && outgoing !== incoming) {
      outgoing.style.transition = ease;
      outgoing.style.opacity = '0';
      outgoing.style.transform = 'scale(1.04)';
      const finishOut = () => {
        outgoing.classList.add('hidden');
        outgoing.style.transition = '';
        outgoing.style.opacity = '';
        outgoing.style.transform = '';
      };
      // Timer (not transitionend) so a missed/interrupted event can't leave a
      // layer stuck visible. +40ms is a safety buffer past the transition end.
      setTimeout(finishOut, DURATION + 40);
    }
    setTimeout(clearIncoming, DURATION + 40);
  }
}

// Normalize a color/variant string to a Shopify handle so it matches the
// `data-variant-color` attributes (rendered with the Liquid `| handleize`).
// Mirrors the handleize used in snippets/bundle-included-items.liquid.
function bbHandleize(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Bind the main-color → thumbnail crossfade ONCE at the document level. The
// <bundle-builder> element is recreated on every variant-change re-render
// (main.js → updateDom), so a per-element listener would leak; instead we
// keep a single listener and drive whichever builder(s) are live in the DOM.
if (!window.__bbColorSyncBound) {
  window.__bbColorSyncBound = true;
  document.addEventListener('variant:swatch', (e) => {
    const detail = (e && e.detail) || {};
    const colorHandle = bbHandleize(detail.value || detail.option || '');
    if (!colorHandle) return;
    document.querySelectorAll('bundle-builder').forEach((bb) => {
      if (typeof bb.syncBaseImagesToColor === 'function') {
        bb.syncBaseImagesToColor(colorHandle);
      }
      if (typeof bb.syncSoldOutState === 'function') {
        bb.currentColorHandle = colorHandle;
        bb.syncSoldOutState();
      }
    });
  });
  document.addEventListener('update:affirm', () => {
    document.querySelectorAll('bundle-builder').forEach((bb) => {
      if (typeof bb.syncSoldOutState === 'function') bb.syncSoldOutState();
    });
  });
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
